/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useState } from "react";

//@Packages
import {
  Button,
  TrashIcon
} from "@forge/common";
import { useTranslation } from "react-i18next";

//@Components
import CustomizedTable from "./customizedtable";

//@Models
import {
  OverviewTableHeaderProps
} from "../../../../../models/overviewtable";
import { ISiteListProps } from "../../../../../models";
import { GatewaySiteProps } from "../../../../../models/devicelist";

//@Static Data
import {
  overviewTableTableHeaders
} from "../../../../../utils/staticdata";

//@Styles
import "./styles.scss";

interface DownstreamDeviceListProps {
  title?: string;
  deviceId?: string;
  isEdit?: boolean;
  isDisabled?: boolean;
  data?: ISiteListProps[];
  selectedData?: GatewaySiteProps[];
  setSiteValueSelection?: (lists: GatewaySiteProps[]) => void;
  isSitesOrgAvailable?: boolean;
  sitesAlreadyAssoData?: ISiteListProps;
}

//@Functional component
const OverviewTable: React.FC<DownstreamDeviceListProps> = ({
  isEdit,
  isDisabled,
  data,
  title,
  selectedData,
  setSiteValueSelection,
  isSitesOrgAvailable,
  sitesAlreadyAssoData
}) => {
  const { t } = useTranslation();

  const convertFn = (value: string) => t(value);
  const siteListHeaders = overviewTableTableHeaders(isEdit, convertFn);

  const [deviceLists, setDeviceLists] = useState<ISiteListProps[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [tableHeaders, settableHeaders] = useState<OverviewTableHeaderProps[]>(siteListHeaders);
  const [filterHeaders, setFilterHeaders] = useState<
    OverviewTableHeaderProps[]
  >(siteListHeaders);
  const SCROLLABLE_THRESHOLD = 15;

  useEffect(() => {
    setDeviceLists(selectedData);
  }, [selectedData]);

  useEffect(() => {
    const getFilterData = tableHeaders?.filter(
      (item: OverviewTableHeaderProps) => item.ischecked
    );
    setFilterHeaders(getFilterData);
  }, [tableHeaders]);

  const formatContentData = (content: ISiteListProps[]) => {
    const formatCellContent = (value: any) => value || "-";

    return content?.map((item: ISiteListProps, index: number) => {
      return {
        id: (index + 1).toString(),
        cell: filterHeaders.map((header) => {
          let cellContent: any = (
            <span>{formatCellContent(item[header.id])}</span>
          );

          if (header.id === "action") {
            //check old or new site 
            const isOldSite = Array.isArray(sitesAlreadyAssoData)
              ? sitesAlreadyAssoData.some((old: ISiteListProps) => old.id === item.id)
              : sitesAlreadyAssoData?.id === item.id;

            cellContent = (
              <div className="gateway-action-button-container">
                {<Button
                  aria-label="Settings"
                  data-testid="delete-device"
                  icon={
                    <TrashIcon
                      strokeWidth={2.5}
                      color={`${isOldSite ? '' : 'var(--link-name-color)'}`}

                    />
                  }
                  variant="quiet-secondary"
                  onClick={() => {
                    const tempData = selectedData.filter((prevItem) => prevItem.id !== item.id);

                    setSiteValueSelection(tempData);
                  }}
                  disabled={isOldSite}
                />}
              </div>
            );
          }

          return { content: cellContent };
        })
      };
    });
  };


  /**
 *
 * To filter the table data based on the search value
 * @param event search event
 */
  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();
  const searchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    const filteredData = selectedData.filter((item) => {
      return normalizeSpaces(item.name).includes(normalizeSpaces(event.target.value));
    });
    setDeviceLists(filteredData);
  };

  const onSearchClear = () => {
    setTimeout(() => {
      setDeviceLists(selectedData);
    }, 1000);
    setSearchValue("");
  };

  return (
    <>
      <div
        data-testid="linked-devices-list-container"
        className={
          deviceLists?.length > SCROLLABLE_THRESHOLD
            ? "scrollable-container"
            : ""
        }
      >
        <CustomizedTable
          title={title || ""}
          tableHeaderData={filterHeaders}
          tableContentData={formatContentData(deviceLists)} // Change After API Integration
          tableData={deviceLists} // Change After API Integration
          searchOnChange={searchOnChange}
          searchValue={searchValue}
          onSearchClear={onSearchClear}
          isEdit={isEdit}
          isDisabled={isDisabled}
          selectedData={data || []}
          setSiteValueSelection={setSiteValueSelection}
          isSitesOrgAvailable={isSitesOrgAvailable}
          sitesAlreadyAssoData={sitesAlreadyAssoData}
        />
      </div>
    </>
  );
};

export default OverviewTable;
